// src/features/analytics/broadcasts.ts
//
// Pure shaping for the broadcast picker (dashboard ADR-009, amended
// 2026-09-13): option values, labels, lookup, and the run shaping. Broadcast
// rows come newest-first from the gateway as VIEWER-PRESENCE runs (≤ 200,
// gaps ≤ 10 min bridged server-side), not on-air publish events — so the
// client re-joins runs a longer lull split, drops noise, and recognizes an
// always-on channel (no separate broadcasts to pick). Publish-event
// broadcasts are the backend follow-up (plan §9).

import { DateTime } from 'luxon';
import {
  BROADCAST_CONTINUOUS_COVERAGE_RATIO,
  BROADCAST_CONTINUOUS_RUN_HOURS,
  BROADCAST_IN_PROGRESS_GRACE_SECONDS,
  BROADCAST_LOOKBACK_DAYS,
  BROADCAST_MERGE_GAP_SECONDS,
  BROADCAST_MIN_MINUTES,
} from '@/lib/constants/analytics';
import { SECONDS_PER_DAY, type DayZone } from '@/shared/utils/utcRange';
import { AnalyticsUtils } from './AnalyticsUtils';
import type { BroadcastRow } from './types';

/** Stable option value — the run's start instant (unique per media). */
export function broadcastValue(row: BroadcastRow): string {
  return String(row.start);
}

/** Find a row by its option value; null when the value names no row (stale URL). */
export function findBroadcast(rows: BroadcastRow[], value: string | null): BroadcastRow | null {
  if (!value) return null;
  return rows.find((r) => broadcastValue(r) === value) ?? null;
}

/**
 * "Fri, Sep 11, 7:02 PM – 9:30 PM · 148 min · peak 212" — times in the viewer's
 * zone (a broadcast is a point in time), duration = the run's span including
 * bridged gaps, peak = minute-exact concurrent viewers.
 */
export function broadcastOptionLabel(row: BroadcastRow, zone: DayZone = 'local', nowSeconds?: number): string {
  const z = zone === 'utc' ? 'utc' : zone;
  const start = DateTime.fromSeconds(row.start, { zone: z });
  const end = DateTime.fromSeconds(row.end, { zone: z });
  const span = start.hasSame(end, 'day')
    ? `${start.toFormat('ccc, MMM d, h:mm a')} – ${end.toFormat('h:mm a')}`
    : `${start.toFormat('ccc, MMM d, h:mm a')} – ${end.toFormat('ccc, MMM d, h:mm a')}`;
  const live = nowSeconds !== undefined && isBroadcastInProgress(row, nowSeconds) ? ' · In progress' : '';
  return `${span} · ${row.minutes} min · peak ${AnalyticsUtils.formatCount(row.peakConcurrent)}${live}`;
}

/** A run whose end is within the grace of now is still on the air (presence is minute-grained). */
export function isBroadcastInProgress(row: BroadcastRow, nowSeconds: number): boolean {
  return row.end >= nowSeconds - BROADCAST_IN_PROGRESS_GRACE_SECONDS;
}

export function anyBroadcastInProgress(rows: readonly BroadcastRow[] | undefined, nowSeconds: number): boolean {
  return !!rows && rows.some((r) => isBroadcastInProgress(r, nowSeconds));
}

/**
 * The picker's request range: the last BROADCAST_LOOKBACK_DAYS, raised to the
 * product-window floor when configured (the gateway refuses ranges that begin
 * before it). `toSeconds` is the caller's hour-stable "now" so the query key
 * does not change on every render.
 */
export function broadcastsLookbackRange(
  toSeconds: number,
  floorSeconds: number | null
): { from: number; to: number; floored: boolean } {
  const natural = toSeconds - BROADCAST_LOOKBACK_DAYS * SECONDS_PER_DAY;
  const floored = floorSeconds !== null && floorSeconds > natural;
  return { from: floored ? floorSeconds : natural, to: toSeconds, floored };
}

/** The empty-list copy: names the real floor when it, not the lookback, bounds the list. */
export function broadcastsEmptyCopy(floored: boolean, floorSeconds: number | null, zone: DayZone = 'local'): string {
  if (floored && floorSeconds !== null) {
    const z = zone === 'utc' ? 'utc' : zone;
    return `No activity since ${DateTime.fromSeconds(floorSeconds, { zone: z }).toFormat('MMM d')}`;
  }
  return `No activity in the last ${BROADCAST_LOOKBACK_DAYS} days`;
}

export type BroadcastPickerState = 'loading' | 'error' | 'empty' | 'ready';

/** The picker's single source of truth for its placeholder/disabled state (walked in tests). */
export function broadcastPickerState(isLoading: boolean, isError: boolean, rowCount: number): BroadcastPickerState {
  if (isError) return 'error';
  if (isLoading) return 'loading';
  return rowCount === 0 ? 'empty' : 'ready';
}

export function broadcastPickerPlaceholder(state: BroadcastPickerState, emptyCopy: string): string {
  switch (state) {
    case 'loading':
      return 'Loading activity…';
    case 'error':
      return "Couldn't load activity";
    case 'empty':
      return emptyCopy;
    default:
      return 'Pick an activity window';
  }
}

/** The row whose window equals an explicit wfrom/wto pair (with or without the hour pad), or null. */
export function broadcastMatchingWindow(
  rows: BroadcastRow[],
  from: number,
  to: number,
  padSeconds: number
): BroadcastRow | null {
  return (
    rows.find(
      (r) =>
        (r.start === from && r.end === to) ||
        (r.start - padSeconds === from && r.end + padSeconds === to)
    ) ?? null
  );
}

export interface ShapedBroadcasts {
  /** Merged, noise-free runs, newest first. */
  rows: BroadcastRow[];
  /** True for an always-on stream: the picker is hidden and the page says why. */
  continuous: boolean;
}

/**
 * Merge presence runs separated by ≤ BROADCAST_MERGE_GAP_SECONDS (start =
 * earliest start, end = latest end, minutes = the span, peak = the max and its
 * time), then drop merged runs under BROADCAST_MIN_MINUTES. Input order does
 * not matter; output is newest first. A stream is `continuous` when any merged
 * run lasts ≥ BROADCAST_CONTINUOUS_RUN_HOURS or the merged runs cover ≥
 * BROADCAST_CONTINUOUS_COVERAGE_RATIO of the lookback span.
 */
export function shapeBroadcasts(
  rows: readonly BroadcastRow[],
  lookback: { from: number; to: number }
): ShapedBroadcasts {
  const sorted = [...rows].sort((a, b) => a.start - b.start);
  const merged: BroadcastRow[] = [];
  for (const row of sorted) {
    const last = merged[merged.length - 1];
    if (last && row.start - last.end <= BROADCAST_MERGE_GAP_SECONDS) {
      const end = Math.max(last.end, row.end);
      const peakWins = row.peakConcurrent > last.peakConcurrent;
      merged[merged.length - 1] = {
        start: last.start,
        end,
        minutes: Math.round((end - last.start) / 60),
        peakConcurrent: peakWins ? row.peakConcurrent : last.peakConcurrent,
        peakAt: peakWins ? row.peakAt : last.peakAt,
      };
    } else {
      merged.push({ ...row, minutes: Math.round((row.end - row.start) / 60) });
    }
  }
  const kept = merged.filter((r) => r.minutes >= BROADCAST_MIN_MINUTES);
  const longest = kept.reduce((m, r) => Math.max(m, r.end - r.start), 0);
  const covered = kept.reduce((sum, r) => sum + (Math.min(r.end, lookback.to) - Math.max(r.start, lookback.from)), 0);
  const span = Math.max(1, lookback.to - lookback.from);
  const continuous =
    kept.length > 0 &&
    (longest >= BROADCAST_CONTINUOUS_RUN_HOURS * 3600 || covered / span >= BROADCAST_CONTINUOUS_COVERAGE_RATIO);
  return { rows: kept.reverse(), continuous };
}

/** The line shown instead of the picker on an always-on stream. */
export const CONTINUOUS_STREAM_COPY =
  'This stream has viewers around the clock, so there are no separate activity windows to pick. Use Day or Range.';
